import { G } from '@mobily/ts-belt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { ReactElement, useCallback, useEffect } from 'react';
import {
  BooleanInput,
  Labeled,
  required,
  SelectInput,
  TextInput,
  useGetOne,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { PostB2bAdminsEndpoint } from '@Common/types/bo/b2b/admins';
import { GetB2bCompanyDetailsResponse } from '@Common/types/bo/b2b/companies';
import { validateEmailWithDomains } from '@Common/validators/email_matching_domain_validation';

import { adminRoleChoices, languagePreferenceChoices } from '@sh4res/bo-common/choices/admins';
import {
  CreateInDialogButton,
  StepperForm,
  StepperFormStep,
  TypographyTranslated,
} from '@sh4res/bo-common/components';
import { AdminConfirmationStep } from '@sh4res/bo-common/components/B2bAdmins/AdminConfirmationStep';
import { AdminCorrespondenceStep } from '@sh4res/bo-common/components/B2bAdmins/AdminCorrespondenceStep';
import {
  ThirdPartyMandateFields,
  transformThirdPartyMandate,
} from '@sh4res/bo-common/components/B2bAdmins/ThirdPartyMandateFields';
import { genderChoices } from '@sh4res/bo-common/components/B2bEmployee/choices';
import { B2bCompanySelectInput, IdField, PhoneNumberInput } from '@sh4res/bo-common/fields';
import { useResourceBasePath, useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import { trimmedEmail } from '@sh4res/bo-common/validators';

export function B2bAdminCreate({
  record,
  icon = <PersonAddIcon />,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  record?: Partial<PostB2bAdminsEndpoint['Client']['RequestBody']>;
  icon?: ReactElement;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const resourceBasePath = useResourceBasePath();
  const { upload } = useUploadPresignedUrl();

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.admins.actions.create.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(
    (response: PostB2bAdminsEndpoint['Client']['ResponseBody']) => {
      notify('resources.bo_common.b2b.admins.actions.create.success', { type: 'success' });
      refresh();
      redirect(
        buildLink(`${resourceBasePath}companies`, 'show', response.company_id, { tab: 'admins' }),
      );
    },
    [notify, redirect, refresh, resourceBasePath],
  );
  const companyId =
    typeof record?.company_id === 'string' ? parseInt(record.company_id) : record?.company_id;

  if (G.isNullable(record) || G.isNullable(companyId)) {
    return null;
  }

  const testId = 'BoExternalB2bAdminCreate';

  const steps: StepperFormStep[] = [
    {
      label: 'resources.bo_common.b2b.admins.steps.general',
      hideDraft: true,
      content: (
        <>
          {G.isNotNullable(record.company_id) ? (
            <Labeled label="resources.bo_common.b2b.admins.fields.company_id">
              <IdField
                source="company_id"
                reference={`${resourceBasePath}companies`}
                data-testid={`${testId}_IdInput`}
              />
            </Labeled>
          ) : (
            <B2bCompanySelectInput
              testId={`${testId}_CompanyInput`}
              source="company_id"
              validate={[required()]}
              isRequired
            />
          )}
          <TextInput
            data-testid={`${testId}_RoleInCompanyInput`}
            source="role_in_company"
            defaultValue={null}
          />
          <EmailAndThirdPartySection companyId={companyId} />
          <BooleanInput
            data-testid={`${testId}_IsActiveInput`}
            source="is_active"
            label="resources.bo_common.b2b.contracts.fields.is_active"
            defaultValue={true}
          />
          <RoleSelectInput testId={`${testId}_RoleSelectInput`} />
          <ThirdPartyMandateFields testId={testId} />
          <SelectInput
            data-testid={`${testId}_GenderInput`}
            source="gender"
            choices={genderChoices}
            defaultValue={null}
          />
          <TextInput
            data-testid={`${testId}_FirstnameInput`}
            source="firstname"
            validate={[required()]}
            isRequired
            defaultValue={null}
          />
          <TextInput
            data-testid={`${testId}_LastnameInput`}
            source="lastname"
            validate={[required()]}
            isRequired
            defaultValue={null}
          />
          <PhoneNumberField testId={`${testId}_PhoneInput`} />
          <SelectInput
            data-testid={`${testId}_LanguageInput`}
            source="language_preference"
            choices={languagePreferenceChoices}
            defaultValue={'FR'}
            validate={[required()]}
            isRequired
          />
        </>
      ),
    },
    {
      label: 'resources.bo_common.b2b.admins.steps.addresses',
      hideDraft: true,
      content: <AdminCorrespondenceStep testId={testId} />,
    },
    {
      label: 'resources.bo_common.b2b.admins.steps.confirmation',
      hideDraft: true,
      content: <AdminConfirmationStep testId={testId} />,
    },
  ];

  return (
    <CreateInDialogButton
      testId={`${testId}_OpenButton`}
      resource={`${resourceBasePath}admins`}
      fullWidth
      maxWidth="md"
      title="resources.bo_common.b2b.admins.actions.create.title"
      mutationOptions={{ onSuccess, onError }}
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'none',
        },
        ...props,
      }}
      icon={icon}
      record={record}
      transform={async (data: Partial<PostB2bAdminsEndpoint['Client']['RequestBody']>) =>
        transformThirdPartyMandate(data, upload)
      }>
      <StepperForm
        steps={steps}
        permissions={{
          resource: `${resourceBasePath}admins`,
          action: 'create',
        }}
        testId={testId}
      />
    </CreateInDialogButton>
  );
}

function getRoleChoices(isThirdParty: boolean) {
  if (isThirdParty) {
    // bo_external third parties can only be level 1, the internal BO can also create level 3
    return adminRoleChoices.filter((choice) =>
      isBoExternal()
        ? choice.id === 'admin_level_1'
        : choice.id === 'admin_level_1' || choice.id === 'admin_level_3',
    );
  }
  if (isBoExternal()) {
    return adminRoleChoices.filter((choice) => choice.id !== 'admin_level_4');
  }
  return adminRoleChoices;
}

function PhoneNumberField({ testId }: { testId: string }) {
  // Phone number is mandatory for third party admins
  const isThirdParty = useWatch({ name: 'is_third_party' });

  return (
    <PhoneNumberInput
      testId={testId}
      source="phone_number"
      defaultValue={null}
      mobileOnly
      validate={isThirdParty ? [required()] : []}
      isRequired={isThirdParty}
    />
  );
}

function RoleSelectInput({ testId }: { testId: string }) {
  const isThirdParty = useWatch({ name: 'is_third_party' });
  const role = useWatch({ name: 'role' });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (isThirdParty && role !== 'admin_level_1' && role !== 'admin_level_3') {
      setValue('role', 'admin_level_1');
    }
  }, [isThirdParty, role, setValue]);

  return (
    <SelectInput
      source="role"
      validate={[required()]}
      isRequired
      defaultValue="admin"
      data-testid={testId}
      choices={getRoleChoices(isThirdParty)}
    />
  );
}

function EmailAndThirdPartySection({ companyId }: { companyId: number }) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const { trigger } = useFormContext();
  const isThirdParty = useWatch({ name: 'is_third_party' });

  useEffect(() => {
    void trigger('email');
  }, [isThirdParty, trigger]);

  const { data: companyDetails } = useGetOne<GetB2bCompanyDetailsResponse>(
    `${resourceBasePath}companies`,
    {
      id: companyId,
    },
    {
      enabled: G.isNotNullable(companyId),
    },
  );

  if (G.isNullable(companyDetails)) {
    return;
  }

  return (
    <>
      <TextInput
        source="email"
        data-testid="BoExternalB2bAdminCreate_EmailInput"
        validate={[
          required(),
          trimmedEmail(),
          ...(!isThirdParty
            ? [
                validateEmailWithDomains(
                  companyDetails.domains,
                  translate(
                    'resources.bo_common.b2b.companies.sections.legal_representative.fields.error_email_domains',
                    { domains: companyDetails.domains },
                  ),
                ),
              ]
            : []),
        ]}
        isRequired
      />
      <BooleanInput
        source="is_third_party"
        defaultValue={false}
        data-testid="BoExternalB2bAdminCreate_IsThirdPartyInput"
      />
      {isThirdParty && (
        <TypographyTranslated
          variant="body2"
          label="resources.bo_common.b2b.admins.third_party_disclaimer"
        />
      )}
    </>
  );
}
